// upload-queue.js - 업로드 대기열 관리
// 네트워크 끊김 시 촬영한 사진 손실 방지

import { t } from './i18n/index.js';

const QUEUE_KEY = 'scantodesk_upload_queue';
const MAX_QUEUE_SIZE = 10; // 최대 10개까지 저장 (localStorage 용량 고려)
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24시간 후 자동 삭제

/**
 * 업로드 대기열 관리자
 */
export const uploadQueue = {
  /**
   * 대기열에서 모든 항목 가져오기
   * @returns {Array<{id: string, pdfBase64: string, fileName: string, addedAt: number}>}
   */
  getAll() {
    try {
      const stored = localStorage.getItem(QUEUE_KEY);
      if (!stored) return [];

      const queue = JSON.parse(stored);
      const now = Date.now();

      // 오래된 항목 자동 정리 (24시간 이상)
      const validItems = queue.filter(item => (now - item.addedAt) < MAX_AGE_MS);

      // 정리된 항목이 있으면 저장
      if (validItems.length !== queue.length) {
        this._save(validItems);
      }

      return validItems;
    } catch (e) {
      console.error('Failed to get upload queue:', e);
      return [];
    }
  },

  /**
   * 대기열에 항목 추가
   * @param {Blob} pdfBlob - PDF Blob
   * @param {string} fileName - 파일명
   * @returns {Promise<string>} - 항목 ID
   */
  async add(pdfBlob, fileName) {
    try {
      const queue = this.getAll();

      // 용량 초과 시 가장 오래된 항목 제거
      while (queue.length >= MAX_QUEUE_SIZE) {
        queue.shift();
      }

      // Blob을 Base64로 변환
      const pdfBase64 = await this._blobToBase64(pdfBlob);

      const id = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      queue.push({
        id,
        pdfBase64,
        fileName,
        addedAt: Date.now()
      });

      this._save(queue);
      console.log(`Added to upload queue: ${fileName} (${queue.length} items in queue)`);

      return id;
    } catch (e) {
      console.error('Failed to add to upload queue:', e);
      throw new Error(t('upload.error.saveFailed'));
    }
  },

  /**
   * 대기열에서 항목 제거
   * @param {string} id - 항목 ID
   */
  remove(id) {
    try {
      const queue = this.getAll();
      const filtered = queue.filter(item => item.id !== id);
      this._save(filtered);
      console.log(`Removed from upload queue: ${id}`);
    } catch (e) {
      console.error('Failed to remove from upload queue:', e);
    }
  },

  /**
   * 파일명으로 항목 제거
   * @param {string} fileName - 파일명
   */
  removeByFileName(fileName) {
    try {
      const queue = this.getAll();
      const filtered = queue.filter(item => item.fileName !== fileName);
      this._save(filtered);
    } catch (e) {
      console.error('Failed to remove from upload queue by fileName:', e);
    }
  },

  /**
   * 대기 중인 항목 수
   * @returns {number}
   */
  getPendingCount() {
    return this.getAll().length;
  },

  /**
   * 모든 대기 항목 재시도
   * @param {Function} uploadFn - 업로드 함수 (pdfBlob, fileName) => Promise
   * @param {Function} onProgress - 진행률 콜백 (current, total)
   * @param {Function} onItemComplete - 항목 완료 콜백 (item, success, error)
   * @returns {Promise<{success: number, failed: number}>}
   */
  async retryAll(uploadFn, onProgress = () => {}, onItemComplete = () => {}) {
    const queue = this.getAll();
    if (queue.length === 0) {
      return { success: 0, failed: 0 };
    }

    console.log(`Retrying ${queue.length} queued uploads...`);

    let success = 0;
    let failed = 0;

    for (let i = 0; i < queue.length; i++) {
      const item = queue[i];
      onProgress(i + 1, queue.length);

      try {
        // Base64를 Blob으로 변환
        const pdfBlob = this._base64ToBlob(item.pdfBase64, 'application/pdf');

        // 업로드 시도
        await uploadFn(pdfBlob, item.fileName);

        // 성공 시 대기열에서 제거
        this.remove(item.id);
        success++;
        onItemComplete(item, true, null);

        console.log(`Retry success: ${item.fileName}`);
      } catch (e) {
        failed++;
        onItemComplete(item, false, e);
        console.error(`Retry failed: ${item.fileName}`, e);

        // 세션 관련 에러는 더 이상 시도하지 않음
        if (e.requiresRepairing) {
          console.log('Session error, stopping retries');
          break;
        }
      }
    }

    return { success, failed };
  },

  /**
   * 대기열 전체 삭제
   */
  clear() {
    localStorage.removeItem(QUEUE_KEY);
    console.log('Upload queue cleared');
  },

  /**
   * 대기열 저장
   * @private
   */
  _save(queue) {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    } catch (e) {
      // localStorage 용량 초과 시 가장 오래된 항목들 제거
      if (e.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, removing old items');
        while (queue.length > 0) {
          queue.shift();
          try {
            localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
            return;
          } catch (e2) {
            // 계속 시도
          }
        }
      }
      throw e;
    }
  },

  /**
   * Blob을 Base64로 변환
   * @private
   */
  _blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },

  /**
   * Base64를 Blob으로 변환
   * @private
   */
  _base64ToBlob(base64, contentType) {
    const parts = base64.split(',');
    const byteCharacters = atob(parts[1] || parts[0]);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }
};

/**
 * 네트워크 복구 시 자동 재시도 설정
 * @param {Function} uploadFn - 업로드 함수
 * @param {Function} onRetryStart - 재시도 시작 콜백
 * @param {Function} onRetryComplete - 재시도 완료 콜백 (result)
 */
export function setupAutoRetry(uploadFn, onRetryStart = () => {}, onRetryComplete = () => {}) {
  window.addEventListener('online', async () => {
    const pendingCount = uploadQueue.getPendingCount();
    if (pendingCount > 0) {
      console.log(`Network online, ${pendingCount} items pending`);
      onRetryStart(pendingCount);

      const result = await uploadQueue.retryAll(uploadFn);
      onRetryComplete(result);
    }
  });

  console.log('Auto retry on network restore enabled');
}
